/**
 * Results Carousel — Carrusel de resultados
 * assets/results-carousel.js
 *
 * – Carrusel infinito con clonado de nodos (patrón UGC).
 * – Snap centrado: la imagen activa se centra con previews laterales.
 * – Texto Antes/Después con animación de entrada al completar deslizamiento.
 * – Autoplay configurable.
 * – SEO-safe: clones neutralizados.
 */

(function () {
  'use strict';

  /**
   * Neutraliza un clon para SEO y accesibilidad.
   * @param {HTMLElement} clone
   */
  function seoSanitizeClone(clone) {
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-nosnippet', '');
    clone.querySelectorAll('a').forEach(a => {
      a.removeAttribute('href');
      a.setAttribute('tabindex', '-1');
      a.setAttribute('role', 'presentation');
    });
    clone.querySelectorAll('img, video').forEach(el => {
      el.setAttribute('role', 'presentation');
      el.removeAttribute('alt');
    });
  }

  /**
   * Finds the card closest to the center of the track viewport.
   * @param {HTMLElement} track
   * @returns {HTMLElement|null}
   */
  function getCenterCard(track) {
    const cards = track.querySelectorAll('.results-carousel__card');
    const trackRect = track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;
    let closest = null;
    let closestDist = Infinity;

    cards.forEach(card => {
      const cardRect = card.getBoundingClientRect();
      const cardCenter = cardRect.left + cardRect.width / 2;
      const dist = Math.abs(cardCenter - trackCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closest = card;
      }
    });

    return closest;
  }

  /**
   * @param {HTMLElement} section
   */
  function initResultsCarousel(section) {
    const track = section.querySelector('.results-carousel__track');
    const prevBtn = section.querySelector('.results-carousel__nav--prev');
    const nextBtn = section.querySelector('.results-carousel__nav--next');
    const labelsEl = section.querySelector('.results-carousel__labels');

    if (!track) return;

    const originalCards = Array.from(track.querySelectorAll('.results-carousel__card'));
    const isInfinite = originalCards.length > 1;
    let setWidth = 0;

    // ── Active card highlighting ──────────────────────────────────────
    let activeCard = null;
    let labelTimer = null;

    const updateActiveCard = (isTeleport = false) => {
      const center = getCenterCard(track);
      if (!center || center === activeCard) return;

      // Remove previous
      if (activeCard) activeCard.classList.remove('is-active');
      activeCard = center;
      activeCard.classList.add('is-active');

      // Animate labels — skip if it's a technical teleport
      if (labelsEl && !isTeleport) {
        labelsEl.classList.remove('is-visible');
        clearTimeout(labelTimer);
        labelTimer = setTimeout(() => {
          labelsEl.classList.add('is-visible');
        }, 200);
      } else if (labelsEl && isTeleport) {
        // Ensure they stay visible during teleport
        labelsEl.classList.add('is-visible');
      }
    };

    // ── Infinite loop ─────────────────────────────────────────────────
    if (isInfinite) {
      const makeCloneSet = () =>
        originalCards.map(card => {
          const clone = card.cloneNode(true);
          clone.classList.add('results-carousel__card--clone');
          seoSanitizeClone(clone);
          return clone;
        });

      // Prepend and append clones
      makeCloneSet().reverse().forEach(clone => track.prepend(clone));
      makeCloneSet().forEach(clone => track.append(clone));

      const computeSetWidth = () => {
        const all = track.querySelectorAll('.results-carousel__card');
        if (all.length < 2) return 0;
        const r1 = all[0].getBoundingClientRect();
        const r2 = all[1].getBoundingClientRect();
        return (r2.left - r1.left) * originalCards.length;
      };

      const initPosition = () => {
        setWidth = computeSetWidth();
        if (setWidth === 0) return;

        // Encontrar la primera tarjeta original (la que no es clon)
        // Está después del primer set de clones
        const allCards = track.querySelectorAll('.results-carousel__card');
        const firstOriginal = allCards[originalCards.length]; 

        if (firstOriginal) {
          track.style.scrollBehavior = 'auto';
          // Centrar la tarjeta: su offset menos la mitad del espacio sobrante del viewport
          const centerOffset = firstOriginal.offsetLeft - (track.clientWidth - firstOriginal.offsetWidth) / 2;
          track.scrollLeft = centerOffset;
        }

        requestAnimationFrame(() => {
          track.style.scrollBehavior = '';
          updateActiveCard();
        });
      };

      // Usar un IntersectionObserver o esperar a que las imágenes carguen para tener offsets válidos
      if (document.readyState === 'complete') {
        initPosition();
      } else {
        window.addEventListener('load', initPosition, { once: true });
      }

      // Teleportation on scroll end
      let teleporting = false;
      const teleportIfNeeded = () => {
        if (teleporting || setWidth === 0) return;
        const maxScroll = track.scrollWidth - track.clientWidth;
        const currentScroll = track.scrollLeft;

        // Umbrales más inteligentes: si nos salimos del "set central"
        // El set central empieza aproximadamente en setWidth y termina en 2*setWidth
        if (currentScroll < setWidth * 0.5) {
          teleporting = true;
          track.classList.add('is-teleporting');
          track.style.scrollBehavior = 'auto';
          track.scrollLeft += setWidth;
          // Actualizar estado activo inmediatamente indicando que es un teleport
          updateActiveCard(true);
          requestAnimationFrame(() => {
            track.style.scrollBehavior = '';
            track.classList.remove('is-teleporting');
            teleporting = false;
          });
        } else if (currentScroll > setWidth * 1.5) {
          teleporting = true;
          track.classList.add('is-teleporting');
          track.style.scrollBehavior = 'auto';
          track.scrollLeft -= setWidth;
          // Actualizar estado activo inmediatamente indicando que es un teleport
          updateActiveCard(true);
          requestAnimationFrame(() => {
            track.style.scrollBehavior = '';
            track.classList.remove('is-teleporting');
            teleporting = false;
          });
        }
      };

      if ('onscrollend' in window) {
        track.addEventListener('scrollend', () => {
          if (!teleporting) {
            teleportIfNeeded();
            updateActiveCard();
          }
        }, { passive: true });
      } else {
        let scrollEndTimer;
        track.addEventListener('scroll', () => {
          if (teleporting) return;
          clearTimeout(scrollEndTimer);
          scrollEndTimer = setTimeout(() => {
            teleportIfNeeded();
            updateActiveCard();
          }, 150);
        }, { passive: true });
      }

      // Also update active on scroll for visual feedback
      let scrollRAF = null;
      track.addEventListener('scroll', () => {
        if (scrollRAF) cancelAnimationFrame(scrollRAF);
        scrollRAF = requestAnimationFrame(updateActiveCard);
      }, { passive: true });

      // Recalculate on resize
      window.addEventListener('resize', () => {
        const prev = setWidth;
        setWidth = computeSetWidth();
        if (prev > 0 && setWidth !== prev && setWidth > 0) {
          track.style.scrollBehavior = 'auto';
          track.scrollLeft = (track.scrollLeft / prev) * setWidth;
          requestAnimationFrame(() => {
            track.style.scrollBehavior = '';
            updateActiveCard();
          });
        }
      }, { passive: true });

    } else {
      // Single or no cards — just mark active
      requestAnimationFrame(() => {
        updateActiveCard();
        if (labelsEl) labelsEl.classList.add('is-visible');
      });
    }

    // ── Navigation buttons ────────────────────────────────────────────
    if (prevBtn && nextBtn) {
      const getCardWidth = () => {
        const card = track.querySelector('.results-carousel__card');
        if (!card) return track.clientWidth * 0.5;
        const style = getComputedStyle(track);
        const gap = parseFloat(style.gap) || 20;
        return card.offsetWidth + gap;
      };

      prevBtn.addEventListener('click', () => {
        track.scrollBy({ left: -getCardWidth(), behavior: 'smooth' });
      });

      nextBtn.addEventListener('click', () => {
        track.scrollBy({ left: getCardWidth(), behavior: 'smooth' });
      });
    }

    // ── Autoplay ──────────────────────────────────────────────────────
    const isAutoplay = section.dataset.autoplay === 'true';
    const autoplaySpeed = parseInt(section.dataset.autoplaySpeed, 10) || 4000;
    let autoplayTimer = null;

    const getCardWidthForAutoplay = () => {
      const card = track.querySelector('.results-carousel__card');
      if (!card) return track.clientWidth * 0.5;
      const style = getComputedStyle(track);
      const gap = parseFloat(style.gap) || 20;
      return card.offsetWidth + gap;
    };

    const startAutoplay = () => {
      if (!isAutoplay || !isInfinite) return;
      stopAutoplay();
      autoplayTimer = setInterval(() => {
        track.scrollBy({ left: getCardWidthForAutoplay(), behavior: 'smooth' });
      }, autoplaySpeed);
    };

    const stopAutoplay = () => {
      if (autoplayTimer) clearInterval(autoplayTimer);
    };

    if (isAutoplay && isInfinite) {
      startAutoplay();
      track.addEventListener('touchstart', stopAutoplay, { passive: true });
      track.addEventListener('mousedown', stopAutoplay, { passive: true });
      track.addEventListener('touchend', () => setTimeout(startAutoplay, 2000), { passive: true });
      track.addEventListener('mouseup', () => setTimeout(startAutoplay, 2000), { passive: true });
    }
  }

  // ── Init ──────────────────────────────────────────────────────────
  function init() {
    document.querySelectorAll('.results-carousel-section').forEach(initResultsCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
